// Admin Apps sub-tab — catalogue + per-app live session count.
// Endpoint: GET /api/admin/apps (admin-gated).

import { esc } from "../lib/format";

interface AppSummary {
  key: string;
  name: string;
  description: string | null;
  base_url: string | null;
  is_active: boolean;
  is_visible: boolean;
  active_sessions: number;
}

async function fetchSummary(): Promise<AppSummary[]> {
  const r = await fetch("/api/admin/apps", {
    credentials: "same-origin",
  });
  if (r.status === 403) {
    throw new Error("403 — admin role required");
  }
  if (!r.ok) {
    throw new Error(`HTTP ${r.status}: ${await r.text()}`);
  }
  return (await r.json()) as AppSummary[];
}

function renderRow(app: AppSummary): string {
  // The Signal app itself doesn't get an outbound logout POST (it IS
  // the platform) — mark it so admins aren't confused by the count.
  const sentinelHint =
    app.key === "signal" ? `<span class="muted">(platform-internal)</span>` : "";
  // SGNL-201: the App Detail page (#apps/{key}) is parked on Signal SPA.
  // Render the key as plain code text — no link. When the standalone
  // Hub project ships at hub.bounteous.tools, this can either become
  // an absolute https://hub.bounteous.tools/#apps/{key} href or be
  // re-introduced once Signal SPA routes #apps/{key} again.
  return `
    <tr data-testid="admin-app-row" data-app-key="${esc(app.key)}">
      <td><code>${esc(app.key)}</code> ${sentinelHint}</td>
      <td>${esc(app.name)}</td>
      <td>${esc(app.base_url ?? "")}</td>
      <td class="num" data-testid="admin-app-sessions">${esc(String(app.active_sessions))}</td>
    </tr>`;
}

export async function renderAdminAppsPanel(host: HTMLElement): Promise<void> {
  host.innerHTML = `
    <p class="muted">
      Catalogue of apps integrated with Signal SSO. The Live Sessions column
      counts non-terminated <code>app_sessions</code> rows per app — i.e.
      how many users are currently inside that app via a Signal handoff.
    </p>
    <div class="admin-apps-host" data-testid="admin-apps-host">
      <p class="loading">Loading…</p>
    </div>`;

  const tableHost = host.querySelector<HTMLElement>(
    '[data-testid="admin-apps-host"]',
  )!;

  let apps: AppSummary[];
  try {
    apps = await fetchSummary();
  } catch (err) {
    tableHost.innerHTML = `<p class="error">Failed to load: ${esc((err as Error).message)}</p>`;
    return;
  }

  if (apps.length === 0) {
    tableHost.innerHTML = `<p class="empty-bucket">No apps registered.</p>`;
    return;
  }

  tableHost.innerHTML = `
    <table class="admin-apps-table" data-testid="admin-apps-table">
      <thead>
        <tr>
          <th>Key</th>
          <th>Name</th>
          <th>Base URL</th>
          <th class="num">Live Sessions</th>
        </tr>
      </thead>
      <tbody>
        ${apps.map(renderRow).join("")}
      </tbody>
    </table>`;
}
